export type Currency = "BRL";

export const ANY_ZONE = "*";

export interface PricingConfig {
  enabled: boolean;
  whatsappNumber: string;
  currency: Currency;
  maxPassengers: number;
  roundTrip: {
    enabled: boolean;
    multiplier: number;
  };
  disclaimer: string;
  updatedAt?: number;
}

export interface Zone {
  name: string;
  order: number;
  active: boolean;
}

export interface Place {
  name: string;
  zoneId: string;
  aliases: string[];
  active: boolean;
}

export interface Tier {
  minPax: number;
  maxPax: number;
  price: number;
}

/**
 * Ponta de uma tarifa. Pode ser:
 * - qualquer origem (`zoneId = ANY_ZONE`, sem placeId)
 * - uma região inteira (`zoneId`, sem placeId)
 * - um local específico (`placeId` + `zoneId` do local)
 */
export interface Route {
  originZoneId: string; // zoneId ou ANY_ZONE
  originPlaceId?: string;
  destinationZoneId: string;
  destinationPlaceId?: string;
  bidirectional: boolean;
  active: boolean;
  tiers: Tier[];
}

export type RouteEndpointKind = "any" | "zone" | "place";

export type ExtraType = "fixed" | "perPax";

export interface Extra {
  name: string;
  price: number;
  type: ExtraType;
  active: boolean;
}

export type WithId<T> = T & { id: string };

export interface PricingData {
  config: PricingConfig;
  zones: Record<string, Zone>;
  places: Record<string, Place>;
  routes: Record<string, Route>;
  extras: Record<string, Extra>;
}

export interface QuoteInput {
  originPlaceId?: string;
  originText?: string;
  destinationPlaceId?: string;
  destinationText?: string;
  passengers: number;
  roundTrip: boolean;
  date?: number;
  flight?: string;
  extraIds: string[];
  name?: string;
  phone?: string;
  notes?: string;
}

export type QuoteStatus = "ok" | "on_request";

export type QuoteReason =
  | "unknown_place"
  | "no_route"
  | "pax_out_of_range"
  | "invalid_passengers";

export type QuoteLineKind = "leg" | "roundTrip" | "extra";

export interface QuoteLine {
  kind: QuoteLineKind;
  label: string;
  amount: number;
  meta?: {
    minPax?: number;
    maxPax?: number;
    multiplier?: number;
    extraId?: string;
    perPax?: boolean;
    unitPrice?: number;
  };
}

export interface QuoteEndpoint {
  placeId?: string;
  name: string;
  zoneId?: string;
  zoneName?: string;
}

export interface QuoteResult {
  status: QuoteStatus;
  reason?: QuoteReason;
  currency: Currency;
  lines: QuoteLine[];
  total: number;
  origin?: QuoteEndpoint;
  destination?: QuoteEndpoint;
  matchedRoute?: {
    id: string;
    originZoneId: string;
    originPlaceId?: string;
    destinationZoneId: string;
    destinationPlaceId?: string;
    reversed: boolean;
    tier: Tier;
  };
}
